import { useState, useEffect, useCallback } from 'react';
import { api, type ToolEntry, type ToolMetric } from '../services/api';

const STATUS_COLORS: Record<string, { dot: string; text: string; bg: string }> = {
  healthy: { dot: 'bg-emerald-400', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  degraded: { dot: 'bg-amber-400', text: 'text-amber-400', bg: 'bg-amber-500/10' },
  down: { dot: 'bg-red-400', text: 'text-red-400', bg: 'bg-red-500/10' },
  disabled: { dot: 'bg-slate-500', text: 'text-slate-500', bg: 'bg-slate-500/10' },
  unknown: { dot: 'bg-slate-600', text: 'text-slate-400', bg: 'bg-slate-500/5' },
};

const CATEGORY_ICONS: Record<string, string> = {
  llm: '🧠',
  search: '🔍',
  scrape: '🌐',
  voice: '🔊',
  channel: '💬',
  storage: '🗄️',
};

function fmtMs(ms?: number) {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export default function Tools() {
  const [tools, setTools] = useState<ToolEntry[]>([]);
  const [summary, setSummary] = useState({ total: 0, healthy: 0, degraded: 0, down: 0, disabled: 0 });
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [selected, setSelected] = useState<ToolEntry | null>(null);
  const [usage, setUsage] = useState<Awaited<ReturnType<typeof api.getToolUsage>> | null>(null);
  const [liveLogs, setLiveLogs] = useState<ToolMetric[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const load = useCallback(async (probe = false) => {
    setLoading(true);
    try {
      const data = await api.getTools(probe);
      setTools(data.tools || []);
      setSummary(data.summary || { total: 0, healthy: 0, degraded: 0, down: 0, disabled: 0 });
      const logsData = await api.getToolLogs({ limit: 20 }).catch(() => ({ logs: [], total: 0 }));
      setLiveLogs(logsData.logs || []);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => load(false), 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.getToolLogs({ limit: 20 });
        setLiveLogs(data.logs || []);
      } catch {
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleProbe = async () => {
    setProbing(true);
    await load(true);
    setProbing(false);
  };

  const handleSelect = async (tool: ToolEntry) => {
    setSelected(tool);
    setUsage(null);
    try {
      const data = await api.getToolUsage(tool.id, '24');
      setUsage(data as any);
    } catch {
    }
  };

  const filtered = tools.filter(t => {
    if (filter && !t.name.toLowerCase().includes(filter.toLowerCase()) && !t.id.toLowerCase().includes(filter.toLowerCase())) return false;
    if (categoryFilter && t.category !== categoryFilter) return false;
    return true;
  });

  const categories = [...new Set(tools.map(t => t.category))];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Tools & Integrations</h1>
          <p className="text-slate-400 text-sm mt-1">Live inventory, health, and usage of every API and service Olu connects to</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${autoRefresh ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' : 'bg-white/5 border-white/10 text-slate-400'}`}
          >
            {autoRefresh ? '● LIVE' : '○ PAUSED'}
          </button>
          <button
            onClick={handleProbe}
            disabled={probing}
            className="px-3 py-2 rounded-lg text-xs font-semibold bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/30 transition-all disabled:opacity-50"
          >
            {probing ? 'Probing...' : '⚡ Probe Now'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: summary.total, color: 'text-white', icon: '📋' },
          { label: 'Healthy', value: summary.healthy, color: 'text-emerald-400', icon: '●' },
          { label: 'Degraded', value: summary.degraded, color: 'text-amber-400', icon: '●' },
          { label: 'Down', value: summary.down, color: 'text-red-400', icon: '●' },
          { label: 'Disabled', value: summary.disabled, color: 'text-slate-500', icon: '○' },
        ].map(s => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wider">{s.label}</div>
            <div className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Filter tools..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500/50"
        />
        <select
          value={categoryFilter}
          onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-cyan-500/50"
        >
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 text-center py-12 text-slate-500">Loading tools...</div>
        ) : filtered.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-slate-500">No tools found</div>
        ) : filtered.map(tool => {
          const sc = STATUS_COLORS[tool.status] || STATUS_COLORS.unknown;
          const u = tool.usage_24h;
          return (
            <button
              key={tool.id}
              onClick={() => handleSelect(tool)}
              className={`text-left bg-white/5 border rounded-xl p-5 hover:bg-white/10 transition-all ${selected?.id === tool.id ? 'border-cyan-500/50 shadow-[0_0_20px_rgba(6,182,212,0.1)]' : 'border-white/10'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{CATEGORY_ICONS[tool.category] || '🔧'}</span>
                  <div>
                    <div className="font-semibold text-white">{tool.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{tool.id}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${sc.dot} ${tool.status === 'healthy' || tool.status === 'degraded' ? 'animate-pulse' : ''}`} />
                  <span className={`text-xs font-semibold uppercase ${sc.text}`}>{tool.status}</span>
                </div>
              </div>
              {tool.description && <p className="text-sm text-slate-400 mt-2">{tool.description}</p>}
              <div className="grid grid-cols-4 gap-2 mt-4">
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Calls</div>
                  <div className="text-sm font-semibold text-white">{u?.total_calls || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Errors</div>
                  <div className={`text-sm font-semibold ${u?.error_count ? 'text-red-400' : 'text-white'}`}>{u?.error_count || 0}</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Err%</div>
                  <div className={`text-sm font-semibold ${(u?.error_rate || 0) > 5 ? 'text-red-400' : (u?.error_rate || 0) > 0 ? 'text-amber-400' : 'text-white'}`}>{(u?.error_rate || 0).toFixed(1)}%</div>
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 uppercase">Avg</div>
                  <div className="text-sm font-semibold text-white">{fmtMs(u?.avg_latency_ms)}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selected && usage && (
        <div className="bg-white/5 border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">{selected.name} — Usage (24h)</h2>
            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white text-sm">✕ close</button>
          </div>
          {usage.usage.by_hour && usage.usage.by_hour.length > 0 && (() => {
            const max = Math.max(...usage.usage.by_hour.map(h => h.calls), 1);
            return (
              <div className="space-y-1">
                {usage.usage.by_hour.map((h) => (
                  <div key={h.hour} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 w-32 font-mono">{h.hour.slice(11, 16)}</span>
                    <div className="flex-1 bg-white/5 rounded h-4 overflow-hidden flex">
                      <div className="bg-cyan-500/60 h-full" style={{ width: `${(h.calls / max) * 100}%` }} />
                      {h.errors > 0 && <div className="bg-red-500/60 h-full" style={{ width: `${(h.errors / max) * 100}%` }} />}
                    </div>
                    <span className="text-slate-300 w-8 text-right">{h.calls}</span>
                    {h.errors > 0 && <span className="text-red-400 w-8 text-right">{h.errors}e</span>}
                  </div>
                ))}
              </div>
            );
          })()}
          {usage.recent_metrics && usage.recent_metrics.length > 0 && (
            <div className="mt-6">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Recent calls</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {usage.recent_metrics.slice(0, 10).map(m => (
                  <div key={m.id} className="flex items-center gap-3 text-xs font-mono">
                    <span className="text-slate-500">{new Date(m.created_at).toLocaleTimeString()}</span>
                    <span className={m.status_code < 400 ? 'text-emerald-400' : 'text-red-400'}>[{m.status_code}]</span>
                    <span className="text-slate-300 w-16">{fmtMs(m.latency_ms)}</span>
                    <span className="text-slate-500">{m.channel}</span>
                    {m.error_message && <span className="text-red-400 truncate">{m.error_message.slice(0, 60)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-white">Live Log Stream</h2>
          <span className="text-xs text-slate-500">{liveLogs.length} entries</span>
        </div>
        {liveLogs.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">No logs yet. Send a chat to generate telemetry.</div>
        ) : (
          <div className="space-y-1 max-h-64 overflow-y-auto font-mono text-xs">
            {liveLogs.map(l => (
              <div key={l.id} className="flex items-center gap-3 py-1">
                <span className="text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleTimeString()}</span>
                <span className={l.status_code < 400 ? 'text-emerald-400' : 'text-red-400'}>[{l.status_code}]</span>
                <span className="text-cyan-400 w-32 truncate">{l.tool_name}</span>
                <span className="text-slate-500 w-20">{l.channel}</span>
                <span className="text-slate-300">{fmtMs(l.latency_ms)}</span>
                {l.error_message && <span className="text-red-400 truncate">{l.error_message.slice(0, 80)}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
